/**
 * Resolves what kind of items the trial limitation applies to, for the
 * upgrade modal/banner copy and for gating the reconciliation proof (which
 * only makes sense for bank/CC transactions).
 *
 * The URL is the source of truth: the tree view (/extractdata) carries
 * ?docType=bank|invoices|forms|brokerage|other and the single-job view
 * (/jobData) carries ?jobType=. The Redux job is only a fallback —
 * job.isBankMode is unreliable mid-flow (JobDataViewer itself uses
 * originalIsBankMode), and in the tree view there may be no job at all.
 */
export function getTrialItemLabels(
  job:
    | {
        type?: string;
        isBankMode?: boolean;
        originalIsBankMode?: boolean;
      }
    | null
    | undefined,
  location: { pathname?: string; search?: string }
): { items: string; itemLabel: string } {
  const params = new URLSearchParams(location.search || "");
  const docType = (params.get("docType") || "").toLowerCase();
  const jobTypeParam = (params.get("jobType") || "").toLowerCase();
  const path = (location.pathname || "").toLowerCase();

  const TRANSACTIONS = { items: "transactions", itemLabel: "transactions" };
  const INVOICES = {
    items: "invoices/receipts",
    itemLabel: "invoices and receipts",
  };
  const FORMS = { items: "forms", itemLabel: "forms" };
  const ROWS = { items: "rows", itemLabel: "rows" };

  if (["bank", "brokerage", "csv2qbo"].includes(docType)) {
    return TRANSACTIONS;
  }
  if (docType === "invoices") {
    return INVOICES;
  }
  if (docType === "forms") {
    return FORMS;
  }
  if (docType === "other") {
    return ROWS;
  }

  // navigateToJobData sets jobType= on /jobData; the tree redirect rewrites
  // bank/brokerage to jobType=ExtractData (both transaction kinds).
  if (["bank", "csv2qbo", "brokerage", "extractdata"].includes(jobTypeParam)) {
    return TRANSACTIONS;
  }
  if (jobTypeParam === "invoice") {
    return INVOICES;
  }
  if (jobTypeParam === "form") {
    return FORMS;
  }
  if (["generic", "template"].includes(jobTypeParam)) {
    return ROWS;
  }

  if (job?.type === "Invoice" || path.includes("invoiceid")) {
    return INVOICES;
  }
  if (
    job?.originalIsBankMode ||
    job?.isBankMode ||
    path.includes("transactions")
  ) {
    return TRANSACTIONS;
  }
  if (job?.type === "Form" || path.includes("forms")) {
    return FORMS;
  }
  if (path.includes("otherdocs") || path.includes("templatedocs")) {
    return ROWS;
  }

  return { items: "items", itemLabel: "items" };
}
